'use client';

import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import styles from './ExpandableText.module.scss';

const messages = defineMessages({
  MORE: { defaultMessage: 'more' },
  LESS: { defaultMessage: 'less' }
});

type ExpandableTextProps = {
  children?: ReactNode;
  text?: string;
  // HTML content that has already been sanitized.
  sanitizedInnerHtml?: string;
  maxLines: number;
  className?: string;
  showMoreBackgroundColor?: string; // used to fade the gradient at the end of the text
  onClick?: () => void;
  expandOnLoad?: boolean;
  scrollMarginTop?: number; // used when the text is collapsed, necessary if there is a fixed header
};

// To keep things simple, if you have a design that looks like truncated expandable text, but it's
// actually a link, use a different component instead.
const ExpandableText = ({
  children,
  text,
  sanitizedInnerHtml,
  maxLines,
  className,
  showMoreBackgroundColor,
  onClick,
  expandOnLoad = false,
  scrollMarginTop = 0
}: ExpandableTextProps) => {
  const hasText = Boolean(text?.length) || Boolean(sanitizedInnerHtml?.length) || children != null;
  const {
    formattedDefaultMessages: { MORE, LESS }
  } = useFormatMessage(messages);
  const [isExpanded, setIsExpanded] = useState(expandOnLoad);
  const [isTextOverflowingContainer, setIsTextOverflowingContainer] = useState(false);
  const textContainer = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(() => {
    if (!isExpanded && textContainer.current) {
      setIsTextOverflowingContainer(textContainer.current.scrollHeight > textContainer.current.clientHeight);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!hasText) {
      return;
    }

    handleResize();
    const debouncedHandleResize = debounce(handleResize, 300);
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      debouncedHandleResize.cancel();
    };
  }, [handleResize, hasText]);

  const onShowClick = () => {
    onClick?.();
    // Scroll to the top of the text container if the text being collapsed so we don't lose it in the viewport.
    if (isExpanded && textContainer.current) {
      textContainer.current.scrollIntoView();
    }
    setIsExpanded(!isExpanded);
  };

  const textContent = useMemo(() => {
    const lineClamp = isExpanded ? undefined : maxLines;
    const sharedAttributes = {
      className: styles.text,
      ref: textContainer,
      style: { WebkitLineClamp: lineClamp, lineClamp, scrollMarginTop } satisfies CSSProperties
    };

    if (sanitizedInnerHtml && !children) {
      return <div {...sharedAttributes} dangerouslySetInnerHTML={{ __html: sanitizedInnerHtml }} />;
    }

    return <div {...sharedAttributes}>{children ?? text}</div>;
  }, [children, isExpanded, maxLines, sanitizedInnerHtml, text, scrollMarginTop]);

  if (!hasText) {
    return null;
  }

  return (
    <div className={classNames(styles.container, className)}>
      {textContent}
      {!isExpanded && isTextOverflowingContainer && (
        <div
          className={classNames(styles.showMore, styles.showMoreButton)}
          style={
            // Override the SCSS gradient (which fades to --color-background-primary) so the fade matches the caller's container.
            showMoreBackgroundColor
              ? { background: `linear-gradient(90deg, var(--color-neutral-transparent) 0%, ${showMoreBackgroundColor} 36.11%)` }
              : undefined
          }
        >
          <button onClick={onShowClick} type="button">
            {MORE}
          </button>
        </div>
      )}
      {isExpanded && (
        <div className={classNames(styles.showMoreButton, styles.showLess)}>
          <button onClick={onShowClick} type="button">
            {LESS}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpandableText;
